/**
 * stores/auth.ts — Zustand auth store.
 *
 * Holds the current authenticated User, TenantUser, and effective permissions
 * for the active tenant. Per frontend-conventions.md §12:
 *   - Only the auth feature writes; everyone else reads
 *   - 401 from any API call clears this store and triggers a redirect
 *   - Never store tokens here — session is an HttpOnly cookie set by the backend
 */

import { create } from 'zustand';

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string | null;
}

export interface AuthTenantUser {
  id: string;
  tenantId: string;
  tenantName: string;
  welcomedAt: string | null;
  celebratedMemberIds: string[];
}

interface AuthState {
  user: AuthUser | null;
  tenantUser: AuthTenantUser | null;
  permissions: ReadonlySet<string>;
  isAuthenticated: boolean;
  canAddHumans: boolean;

  setSession: (params: {
    user: AuthUser;
    tenantUser: AuthTenantUser;
    permissions: readonly string[];
    canAddHumans: boolean;
  }) => void;
  clearSession: () => void;
  setWelcomed: (when: string) => void;
  hasPermission: (slug: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  tenantUser: null,
  permissions: new Set(),
  isAuthenticated: false,
  canAddHumans: false,

  setSession: ({ user, tenantUser, permissions, canAddHumans }) =>
    set({
      user,
      tenantUser,
      permissions: new Set(permissions),
      isAuthenticated: true,
      canAddHumans,
    }),

  clearSession: () =>
    set({
      user: null,
      tenantUser: null,
      permissions: new Set(),
      isAuthenticated: false,
      canAddHumans: false,
    }),

  setWelcomed: (when) =>
    set((state) => ({
      tenantUser: state.tenantUser ? { ...state.tenantUser, welcomedAt: when } : null,
    })),

  hasPermission: (slug) => get().permissions.has(slug),
}));

/**
 * Hook for checking a single permission. Per frontend-conventions.md §12:
 *   const canManage = usePermission('manage_members');
 */
export function usePermission(slug: string): boolean {
  return useAuthStore((s) => s.permissions.has(slug));
}
